import { Injectable, inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { HelpersService, MappingOfStatusesService } from "../shared";
import { CardDisplayService } from "../shared/card-display/card-display.service";
import { EfileStatus, IBaseT1Card, ITranslateObject } from "../views-interfaces";
import { MultiSelectModel, Option } from "./filter-types";

@Injectable({
    providedIn: "root",
})
export class AlertsFilterGeneratorService {
    private readonly helpersService = inject(HelpersService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly translateService = inject(TranslateService);
    private readonly cardDisplayService = inject(CardDisplayService);

    constructor() {}

    generateModel(selection: Option[], viewType: string) {
        const allOptions: Option[] = [
            {
                text: "FiltersSidebar.Options.Alerts.NoAlertCards",
                value: "NoAlertCards",
            },
            {
                text: "FiltersSidebar.Options.Alerts.DocumentsReceivedLate",
                value: "DocumentsReceivedLate",
            },
            {
                text: "FiltersSidebar.Options.Alerts.AlertCards",
                value: "AlertCards",
            },
            {
                text: "FiltersSidebar.Options.Alerts.notEFiled",
                value: "notEFiled",
            },
        ];
        const applicableOptions = allOptions
            .filter((option) => (option.isVisible ? (typeof option.isVisible === "boolean" ? option.isVisible : option.isVisible(viewType)) : true))
            .sort((a, b) => this.translateService.instant(a.text.toString()).localeCompare(this.translateService.instant(b.text.toString())));

        return {
            label: "FiltersSidebar.Labels.Alerts",
            allOptions,
            applicableOptions,
            selection: selection.filter((option) => applicableOptions.some((applicable) => applicable.value === option.value)),
        } as MultiSelectModel;
    }

    getPrintDescription(
        label: string | ITranslateObject,
        selectedOptions: Option[],
    ): { label: string | ITranslateObject; values: (string | ITranslateObject)[] } | null {
        if (!selectedOptions || selectedOptions.length === 0) {
            return null;
        } else {
            return {
                label,
                values: selectedOptions.map((value) => {
                    return value.text;
                }),
            };
        }
    }

    generateFilter(selectedValues: string[]) {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        } else {
            const applicableChecks = selectedValues.reduce(
                (applicable, value) => {
                    if (value === "AlertCards") {
                        applicable.push(this.cardHasAlert.bind(this));
                    } else if (value === "NoAlertCards") {
                        applicable.push(this.cardHasNoAlert.bind(this));
                    } else if (value === "DocumentsReceivedLate") {
                        applicable.push(this.areDocumentsReceivedLate.bind(this));
                    } else if (value === "notEFiled") {
                        applicable.push(this.cardInCompletedAndNotEfiled.bind(this));
                    }
                    return applicable;
                },
                [] as ((card: IBaseT1Card) => boolean)[],
            );

            return (card: IBaseT1Card) => {
                return applicableChecks.some((check) => check(card));
            };
        }
    }

    cardHasAlert(card: IBaseT1Card) {
        const currentStep = this.mappingOfStatusesService.getStepFromStatusId(card.return.statusId);
        if (currentStep) {
            const result = this.cardDisplayService.calcCardFieldLabels(card, this.mappingOfStatusesService.getStatusIdsFromStepId(currentStep.Id));
            return result.isDeadlineAlert || result.isDeadlineWarning || result.isStepAlert || result.isStepWarning;
        } else {
            return false;
        }
    }

    cardHasNoAlert(card: IBaseT1Card) {
        const currentStep = this.mappingOfStatusesService.getStepFromStatusId(card.return.statusId);
        if (currentStep) {
            const result = this.cardDisplayService.calcCardFieldLabels(card, this.mappingOfStatusesService.getStatusIdsFromStepId(currentStep.Id));
            return !result.isDeadlineAlert && !result.isDeadlineWarning && !result.isStepAlert && !result.isStepWarning;
        } else {
            return false;
        }
    }

    cardInCompletedAndNotEfiled(card: IBaseT1Card) {
        const currentStep = this.mappingOfStatusesService.getStepFromStatusId(card.return.statusId);
        return (
            currentStep?.State === "completed" &&
            card.efileStatuses &&
            (card.efileStatuses.fedEfileStatus === EfileStatus.Eligible ||
                card.efileStatuses.fedEfileStatus === EfileStatus.Rejected ||
                card.efileStatuses.provEfileStatus === EfileStatus.Eligible ||
                card.efileStatuses.provEfileStatus === EfileStatus.Rejected)
        );
    }

    areDocumentsReceivedLate(card: IBaseT1Card) {
        if (card.lastYearDocumentsReceivedDate) {
            const oneYearLater = card.lastYearDocumentsReceivedDate + HelpersService.secondsInAYear;
            return oneYearLater <= this.helpersService.convertMillisecondsToSeconds(Date.now()) && !card.documentsReceivedDate;
        } else {
            return false;
        }
    }
}
